import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  BookOpen, Plus, Trash2, ArrowUp, ArrowDown, Loader2, Save, RotateCcw, Eye,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const blankSection = () => ({
  id: `sec_${Math.random().toString(36).slice(2, 9)}`,
  title: '',
  body: '',
});

const VolunteerGuideEditor = ({ canWrite = true }) => {
  const [sections, setSections] = useState([]);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [previewIdx, setPreviewIdx] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/outreach/guide`,
        { withCredentials: true });
      setSections(r.data?.sections || []);
      setMeta({ updated_at: r.data?.updated_at, updated_by: r.data?.updated_by });
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load the guide.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (idx, next) => setSections(prev => prev.map((s, i) => i === idx ? next : s));
  const remove = (idx) => setSections(prev => prev.filter((_, i) => i !== idx));
  const add = () => setSections(prev => [...prev, blankSection()]);
  const move = (idx, delta) => setSections(prev => {
    const next = [...prev];
    const t = idx + delta;
    if (t < 0 || t >= next.length) return prev;
    [next[idx], next[t]] = [next[t], next[idx]];
    return next;
  });

  const save = async () => {
    setSaving(true); setError(''); setToast('');
    try {
      const cleaned = sections
        .map(s => ({ id: s.id, title: (s.title || '').trim(), body: (s.body || '').trim() }))
        .filter(s => s.title || s.body);
      const r = await axios.put(`${BACKEND_URL}/api/admin/outreach/guide`,
        { sections: cleaned }, { withCredentials: true });
      setToast(`Saved · ${r.data.section_count} section${r.data.section_count === 1 ? '' : 's'}`);
      await load();
      setTimeout(() => setToast(''), 3500);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save the guide.');
    } finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    if (!window.confirm('Reset the Volunteer Guide to the default content? Your current edits will be lost.')) return;
    setResetting(true); setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/admin/outreach/guide/reset`,
        {}, { withCredentials: true });
      setToast('Guide reset to defaults.');
      await load();
      setTimeout(() => setToast(''), 3500);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to reset the guide.');
    } finally { setResetting(false); }
  };

  return (
    <div className="space-y-3" data-testid="volunteer-guide-editor">
      <div className="bg-[#FFF8F8] border border-[#BA1826]/30 rounded-lg p-3">
        <p className="text-xs font-bold text-[#BA1826] inline-flex items-center gap-2 mb-1">
          <BookOpen className="w-4 h-4" /> Volunteer Outreach Guide
        </p>
        <p className="text-[11px] text-[#4B5563]">
          This is the cheat sheet volunteers see on their phone when they scan a zone QR.
          Keep the tone community-first and respectful — we&apos;re neighbors building real relationships,
          not salespeople. Edit any section below.
        </p>
        {meta.updated_at && (
          <p className="text-[10px] text-[#9CA3AF] mt-2">
            Last updated {new Date(meta.updated_at).toLocaleString()}
            {meta.updated_by ? ` · by ${meta.updated_by}` : ''}
          </p>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-xs" data-testid="guide-error">{error}</div>}
      {toast && <div className="bg-green-50 border border-green-200 text-green-800 p-2 rounded text-xs" data-testid="guide-toast">{toast}</div>}

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" /></div>
      ) : (
        <>
          {sections.length === 0 && (
            <p className="text-xs text-[#9CA3AF] text-center py-4">
              No sections yet. Click &quot;Reset to defaults&quot; to load NHF&apos;s starter guide,
              or add your own.
            </p>
          )}
          <div className="space-y-2">
            {sections.map((s, i) => (
              <div key={s.id || i} className="border border-[#E5E7EB] rounded p-2 bg-white space-y-1.5" data-testid={`guide-section-${i}`}>
                <div className="flex items-center gap-2">
                  <input value={s.title}
                    onChange={(e) => update(i, { ...s, title: e.target.value })}
                    disabled={!canWrite}
                    placeholder="Section title (e.g. How we introduce NHF)"
                    className="flex-1 px-2 py-1 border border-[#E5E7EB] rounded text-xs font-semibold focus:border-[#BA1826] focus:outline-none"
                    data-testid={`guide-section-title-${i}`} />
                  <div className="flex items-center gap-1">
                    <button onClick={() => setPreviewIdx(previewIdx === i ? null : i)}
                      className="p-1 text-[#4B5563] hover:text-[#0A0A0A]" title="Preview as a volunteer would see it"
                      data-testid={`guide-section-preview-${i}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    {canWrite && (
                      <>
                        <button onClick={() => move(i, -1)} disabled={i === 0}
                          className="p-1 text-[#4B5563] hover:text-[#0A0A0A] disabled:opacity-30"
                          data-testid={`guide-section-up-${i}`}>
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => move(i, 1)} disabled={i === sections.length - 1}
                          className="p-1 text-[#4B5563] hover:text-[#0A0A0A] disabled:opacity-30"
                          data-testid={`guide-section-down-${i}`}>
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => remove(i)}
                          className="p-1 text-red-600 hover:text-red-800"
                          data-testid={`guide-section-delete-${i}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <textarea value={s.body}
                  onChange={(e) => update(i, { ...s, body: e.target.value })}
                  disabled={!canWrite}
                  rows={4}
                  placeholder="What should volunteers say or remember here? Use plain language and bullet points."
                  className="w-full px-2 py-1.5 border border-[#E5E7EB] rounded text-xs focus:border-[#BA1826] focus:outline-none resize-y"
                  data-testid={`guide-section-body-${i}`} />
                {previewIdx === i && (
                  <div className="bg-[#FAFBFC] border border-[#E5E7EB] rounded p-3" data-testid={`guide-section-preview-card-${i}`}>
                    <p className="text-xs font-bold text-[#0A0A0A] mb-1">{s.title || <em className="text-[#9CA3AF]">(no title)</em>}</p>
                    <p className="text-[11px] text-[#4B5563] whitespace-pre-wrap">{s.body || <em className="text-[#9CA3AF]">(no body)</em>}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {canWrite && (
            <button onClick={add}
              className="w-full text-xs px-3 py-2 border border-dashed border-[#E5E7EB] rounded text-[#4B5563] hover:bg-[#F8F9FA] inline-flex items-center justify-center gap-1.5"
              data-testid="guide-add-section">
              <Plus className="w-3 h-3" /> Add a section
            </button>
          )}

          {canWrite && (
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E5E7EB]">
              <button onClick={resetDefaults} disabled={resetting || saving}
                className="text-xs px-3 py-1.5 text-[#4B5563] hover:bg-[#F8F9FA] rounded inline-flex items-center gap-1.5"
                data-testid="guide-reset-btn">
                {resetting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                Reset to defaults
              </button>
              <button onClick={save} disabled={saving || resetting}
                className="text-xs px-3 py-1.5 bg-[#BA1826] text-white rounded hover:bg-[#9F1421] disabled:opacity-50 inline-flex items-center gap-1.5"
                data-testid="guide-save-btn">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save guide
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VolunteerGuideEditor;
